import React from 'react';
import { useAuthContext } from '../../common/contexts/auth';
import {
  getUserFlow,
  getUserFlowResults,
  getUserFlowPageStateMetaData,
  updateUserFlow,
  type UserFlow,
  type UserFlowResults,
  type HttpError
} from '../../common/api-client';

export interface State {
  loading: boolean;
  error: HttpError | null;
  userFlow: UserFlow | null;
  results: UserFlowResults | null;
  updateUserFlow: (
    updates: Partial<Pick<UserFlow, 'name' | 'shared_with'>>
  ) => Promise<void>;
  updating: boolean;
}

const context = React.createContext<State>({
  loading: false,
  error: null,
  userFlow: null,
  results: null,
  updateUserFlow: () => Promise.resolve(),
  updating: false
});

interface UserFlowProviderProps {
  id: string;
  children: React.ReactNode;
}

export const UserFlowProvider: React.FC<UserFlowProviderProps> = ({
  id,
  children
}) => {
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<Error | null>(null);
  const [userFlow, setUserFlow] = React.useState<UserFlow | null>(null);
  const [results, setResults] = React.useState<UserFlowResults | null>(null);
  const [updating, setUpdating] = React.useState(false);
  const user = useAuthContext().user;

  React.useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [loadedUserFlow, loadedResults, userFlowPageStateMetaData] =
          await Promise.all([
            getUserFlow(id, user?.token),
            getUserFlowResults(id, user?.token),
            getUserFlowPageStateMetaData(id, user?.token)
          ]);

        const userFlowPageStateMetaDataMap = userFlowPageStateMetaData.reduce(
          (acc: { [key: string]: string }, curr) => {
            acc[curr.page_state_id] = curr.page_state_name;
            return acc;
          },
          {}
        );

        loadedResults.pageStateResults.forEach(result => {
          result.page_state =
            userFlowPageStateMetaDataMap[result.id] || result.page_state;
        });

        setUserFlow(loadedUserFlow);
        setResults(loadedResults);
      } catch (err) {
        setLoadError(err as HttpError);
      }
      setLoading(false);
    };
    loadData();
  }, [user?.token, id]);

  const updateUserFlowById = async (
    updates: Partial<Pick<UserFlow, 'name' | 'shared_with'>>
  ) => {
    setUpdating(true);
    try {
      // Users who share a user flow should already be authenticated
      if (!user?.token) {
        throw new Error('user required');
      }

      await updateUserFlow(id, user.token, updates);
      // we don't want to reload the entire page when the only value we update from WebApp is `shared_with`
      const loadedUserFlow = await getUserFlow(id, user.token);
      setUserFlow(loadedUserFlow);
      setUpdating(false);
    } catch (err) {
      setUpdating(false);
      throw err;
    }
  };

  return (
    <context.Provider
      value={{
        loading,
        error: loadError,
        userFlow,
        results,
        updateUserFlow: updateUserFlowById,
        updating
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useUserFlow = (): State => React.useContext(context);
